import { Component, inject, OnInit, signal} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, of, Subject, switchMap, takeUntil, timeout } from 'rxjs';
import { sluggify } from '../common/utils';
import { Ecosystem, Tag } from '../models/models';
import { TechService } from '../services/tech.service';
import { SimilarTechsComponent } from '../similar-techs/similar-techs.component';
import { TagComponent } from '../tag/tag.component';
import { TechsStore } from '../core/stores/techs.store';
import { ValidationService, ServerErrors } from '../services/validation.service';
import { FormErrorComponent } from '../form-error/form-error.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-tech-edit',
  templateUrl: './tech-edit.component.html',
  styleUrls: ['./tech-edit.component.css'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, SimilarTechsComponent, TagComponent, FormErrorComponent, MatProgressSpinnerModule]
})

export class TechEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private techService = inject(TechService);
  private formBuilder = inject(FormBuilder);
  private techsStore = inject(TechsStore);
  private validationService = inject(ValidationService);

  ngUnsubscribe = new Subject<void>()

  title: string = "Add Tech";
  tags?: Tag[];
  ecosystems?: Ecosystem[];
  slug?: string;
  isSubmitting  = signal<boolean>(false);
  serverErrors: ServerErrors = {};
  techForm: FormGroup;

  constructor() {
    this.techForm = this.formBuilder.group({
      id: ['', []],
      name: ['', [Validators.required, Validators.minLength(1)]],
      slug: ['', [Validators.required, Validators.minLength(1), Validators.pattern(/^[a-z0-9\-]+$/)]],
      url: ['', [Validators.pattern('^https?://.+')]],
      description: ['',],
      ecosystem: [''],
      tags: [''],
      archived: ['', []],
      imageUrl: ['',[Validators.pattern('^https?://.+')]],
    });
  }

  ngOnInit(): void {
    const handler = ({ data }: MessageEvent) => {
      if (data.messageType === 'fileUploaded') {
        this.techForm.patchValue({
          imageUrl: data.payload.url
        });
      }
    }
    window.addEventListener('message', handler);

    this.route.params.pipe(
      takeUntil(this.ngUnsubscribe),
      switchMap((params) => {
        const slug = params['slug']
        return combineLatest([
          of(slug),
          this.techService.getTagsWithDetail(),
          this.techService.getEcosystems(),
          slug ? this.techService.getTechDetailBySlug(slug) : of(null)
        ])
      }
      )
    ).subscribe({
      next: ([slug, tags, ecosystems, techDetail]) => {
        this.slug = slug;
        if (tags?.data) this.tags = tags.data;
        if (ecosystems?.data) this.ecosystems = ecosystems.data;

        if (this.slug && techDetail?.data) {
          this.title = "Edit Tech";
          this.techForm.patchValue({
            id: techDetail.data.id,
            name: techDetail.data.name,
            description: techDetail.data.description,
            imageUrl: techDetail.data.imageUrl,
            url: techDetail.data.url,
            slug: techDetail.data.slug,
            isArchived: techDetail.data.isArchived,
            tags: techDetail.data.tags || [],
            ecosystem: techDetail.data.ecosystems?.[0]?.id ?? ''
          });
        }
      },
      error: (e) => {
        console.error(e);
      }
    })

  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  submit(): void {
    this.isSubmitting.set(true);
    const techId = this.techForm.get("id")?.value
    const updatedTech = {
      name: this.techForm.get("name")?.value,
      slug: this.techForm.get("slug")?.value,
      description: this.techForm.get("description")?.value,
      imageUrl: this.techForm.get("imageUrl")?.value,
      url: this.techForm.get("url")?.value,
      isArchived: this.techForm.get("isArchived")?.value ?? false,
      ecosystemIds: this.techForm.get("ecosystem")?.value ? [this.techForm.get("ecosystem")?.value] : [],
      tagIds: this.techForm.get("tags")?.value?.map?.((t: Tag) => t.id) || [],

    }
    if (this.slug) {
      this.techService.putTech(techId ?? '-1', updatedTech).subscribe({
        next: async (_) => {
          this.techsStore.getTechs();
          this.isSubmitting.set(false);
          this.validationService.showSnackBar('Tech was updated successfully!', 'success-snackbar');
          this.navigateToPreviousPage();
        },
        error: err => {
          if (err.status === 400 && err.error) {
            if (err.error.errors) {
              this.serverErrors = err.error.errors;
            }
          }
          this.isSubmitting.set(false);
          console.error(err)
        }
      });
    }
    else {
      this.techService.postTech(updatedTech).subscribe({
        next: async (_) => {
          this.techsStore.getTechs();
          this.isSubmitting.set(false);
          this.validationService.showSnackBar('Tech was created successfully!', 'success-snackbar');
          this.navigateToPreviousPage();
        },
        error: err => {
          if (err.status === 400 && err.error) {
            if (err.error.errors) {
              this.serverErrors = err.error.errors;
            }
          }
          this.isSubmitting.set(false);
          console.error(err)
        }
      })
    }

  }
  async navigateToPreviousPage(): Promise<void> {
    await this.router.navigate(['..']);
  }

  onNameChange() {
    const slugFormField = this.techForm.get('slug');
    //only change slug if it hasn't been changed yet
    if (slugFormField?.dirty) return;

    const nameFormFieldValue: string = this.techForm.get('name')?.value;

    const sluggifiedName = sluggify(nameFormFieldValue);

    this.techForm.patchValue({
      slug: sluggifiedName
    });
  }

  selectTag(tagId: string): void {
    if (!this.tags || tagId === '-1') return;
    const foundTag: Tag = this.tags.find(tag => tag.id === tagId) ?? { name: '', id: tagId };
    const formTags: Tag[] = this.techForm.get('tags')?.value || []

    this.techForm.patchValue({ tags: [...(formTags), foundTag] }, {})
  }

  removeSelectedTag(tagId: string): void {
    const formTags: Tag[] = this.techForm.get('tags')?.value || []
    this.techForm.patchValue({ tags: formTags.filter(({ id }) => id !== tagId) }, {})
  }

  async back(): Promise<void> {
    await this.router.navigate([".."]);
  }

  uploadImage() {
    const openFileUploadMessage = {
      messageType: 'openFileUpload',
      payload: {
        appName: 'tech',
      },
    };

    window.parent.postMessage(openFileUploadMessage, '*');
  }

  get allTags(): Tag[] {
    const selectedTagIds = this.techForm.get('tags')?.value?.map?.(({ id }: Tag) => id);
    return this.tags?.filter(({ id }) => !selectedTagIds?.includes(id)) ?? [];

  }
}
